import { jupiterFetch } from '@svs/nova-core/nextjs/jupiter';

import type { Customer } from '../@types/Customer.js';
import type { Product } from '../@types/Product.js';
import { type ErrorResponse, isErrorResponse } from './errorResponse.js';

const getFetchUrl = ({
  activatedDrawId,
  customer,
  product,
  drawNumber,
}: {
  activatedDrawId: number;
  customer: Customer;
  product?: Product;
  drawNumber?: number;
}): string => {
  let url;
  if (customer === 'Group' && product && drawNumber) {
    const qs = new URLSearchParams();
    qs.append('product', product);
    qs.append('drawNumber', drawNumber.toString());
    url = `marketplace/1/activateddraw/balance?${qs}`;
  } else {
    url = `retailer/1/marketplace/activateddraws/${activatedDrawId}`;
  }
  return url;
};

type MarketplaceWallet = {
  activatedDraws: {
    GroupId: number;
    GroupName: string;
    ContainerId: number;
    ActivatedDrawId: number;
    SharePrice: string;
    Balance: string;
    RegClose: string;
    BuyinCloseTime: string;
  }[];
};

type RetailerWallet = {
  activatedDraw: {
    activatedDrawId: number;
    activatedDrawStatus: number;
    activatedDrawDescription: string;
    buyinCloseTime: string;
    maxShares: number;
    commissionPercent: number;
    maxCommission: string;
    maxPlayableAmount: string;
    numSharesRequired: number;
    currentPlayableAmount: string;
    drawNumber: number;
    regCloseTime: string;
    soldShares: number;
    wagerSerial: string | null;
    wagerAmount: string | null;
    retailerCommission: string | null;
    activatedDrawName: string;
    isSubmittable: boolean;
    subProductId: number;
    description: string;
    editorName: string;
    numShares: number;
    sharePrice: string;
    containerId: number;
    editorId: number;
    groupId: number;
    productId: Product;
    name: string;
  };
};

type DTOResponse = {
  balance: number;
  sharePrice: number;
  soldShares: number;
  name?: string;
};

const dto: Record<number, DTOResponse> = {};

/**
 * Fetches the marketplace balance for a given activated draw, product and draw number. If product and draw number are not provided, it fetches the retailer balance for the activated draw.
 *
 * @param root0 object containing the parameters for fetching the marketplace balance
 * @param root0.activatedDrawId the id of the activated draw for which to fetch the balance
 * @param root0.product (optional) the product for which to fetch the balance, required if drawNumber is provided
 * @param root0.drawNumber (optional) the draw number for which to fetch the balance, required if product is provided
 * @param root0.customer the type of customer, either 'Player' | 'Retailer' or 'Group, used to determine how to calculate the number of new shares
 * @param root0.shouldForceFetch (optional) whether to force fetch the balance from the server, default is false
 * @returns the marketplace balance data
 */
export async function fetchMarketplaceBalance({
  activatedDrawId,
  product,
  drawNumber,
  customer,
  shouldForceFetch = false,
}: {
  activatedDrawId: number;
  product?: Product;
  drawNumber?: number;
  customer: Customer;
  shouldForceFetch?: boolean;
}): Promise<DTOResponse | undefined> {
  if (dto[activatedDrawId] && !shouldForceFetch) {
    return dto[activatedDrawId];
  }
  const fetchUrl = getFetchUrl({ activatedDrawId, product, drawNumber, customer });
  const response = await jupiterFetch(fetchUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });
  const data = (await response.json()) as MarketplaceWallet | RetailerWallet | ErrorResponse;

  if (!response.ok || isErrorResponse(data)) {
    return undefined;
  }

  if ('activatedDraws' in data) {
    for (const draw of data.activatedDraws) {
      const balance = Number.parseInt(draw.Balance, 10);
      const sharePrice = Number.parseInt(draw.SharePrice, 10);
      const soldShares = balance && sharePrice ? balance / sharePrice : 0;
      dto[draw.ActivatedDrawId] = {
        balance: Number.parseInt(draw.Balance, 10),
        sharePrice: Number.parseInt(draw.SharePrice, 10),
        soldShares: soldShares,
        name: draw.GroupName,
      };
    }
  }

  if ('activatedDraw' in data) {
    const { activatedDraw } = data;
    const balance = Number.parseInt(activatedDraw.currentPlayableAmount, 10);
    const sharePrice = Number.parseInt(activatedDraw.sharePrice, 10);
    const { soldShares } = activatedDraw;

    const result: DTOResponse = {
      balance: balance,
      sharePrice: sharePrice,
      soldShares,
      name: activatedDraw.name,
    };
    // eslint-disable-next-line require-atomic-updates
    dto[activatedDraw.activatedDrawId] = result;
    return result;
  }

  return dto[activatedDrawId];
}
